import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Save, Undo, Info } from "lucide-react";
import api from "../services/api";

interface Prompt {
  key: string;
  name?: string;
  value?: string;
  description?: string;
  category?: string;
  variables?: string[];
}

interface Message {
  type: "success" | "error";
  text: string;
}

interface PromptEditorProps {
  prompt: Prompt;
  onUpdate?: (key: string, value: string) => void;
}

const PromptEditor: React.FC<PromptEditorProps> = ({ prompt, onUpdate }) => {
  const { t } = useTranslation(["admin"]);
  const [value, setValue] = useState<string>(prompt.value || "");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<Message | null>(null);
  const [showVariables, setShowVariables] = useState<boolean>(false);

  const handleSave = async () => {
    if (!value.trim()) {
      setMessage({ type: "error", text: t("admin:prompts.cannotBeEmpty") });
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      await api.prompts.updatePrompt(prompt.key, value);
      setMessage({
        type: "success",
        text: t("admin:prompts.savedSuccessfully"),
      });
      if (onUpdate) {
        onUpdate(prompt.key, value);
      }
      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
      };
      setMessage({
        type: "error",
        text:
          err.response?.data?.error ||
          err.response?.data?.message ||
          t("admin:prompts.failedToSave"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm(t("admin:prompts.resetConfirm"))) {
      setValue(prompt.value || "");
      setMessage(null);
    }
  };

  const characterCount = value.length;
  const lineCount = value.split("\n").length;

  return (
    <div className="prompt-editor">
      <div className="prompt-editor-header">
        <div className="prompt-info">
          <h3>{prompt.name}</h3>
          <p className="prompt-description">{prompt.description}</p>
          <div className="prompt-meta">
            <span className="prompt-category">{prompt.category}</span>
            {prompt.variables && prompt.variables.length > 0 && (
              <button
                className="btn btn-sm btn-outline"
                onClick={() => setShowVariables(!showVariables)}
                title={t("admin:prompts.showVariables")}
              >
                <Info size={16} />{" "}
                {t("admin:prompts.variables", {
                  count: prompt.variables.length,
                })}
              </button>
            )}
          </div>
        </div>
        <div className="prompt-actions">
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={isSaving || value === prompt.value}
          >
            <Save size={16} />{" "}
            {isSaving ? t("admin:prompts.saving") : t("common:actions.save")}
          </button>
          <button
            className="btn-outline"
            onClick={handleReset}
            disabled={value === prompt.value}
          >
            <Undo size={16} /> {t("common:actions.reset")}
          </button>
        </div>
      </div>

      {showVariables && prompt.variables && prompt.variables.length > 0 && (
        <div className="prompt-variables">
          <h4>{t("admin:prompts.availableVariables")}</h4>
          <div className="variables-list">
            {prompt.variables.map((variable) => (
              <code key={`var-${variable}`} className="variable-tag">
                {"${" + variable + "}"}
              </code>
            ))}
          </div>
          <p className="variables-note">{t("admin:prompts.variablesNote")}</p>
        </div>
      )}

      {message && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      <div className="prompt-editor-body">
        <textarea
          className="prompt-textarea"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t("admin:prompts.placeholder")}
          rows={20}
          spellCheck={false}
        />
        <div className="prompt-stats">
          <span>
            {t("admin:prompts.characters", { count: characterCount })}
          </span>
          <span>{t("admin:prompts.lines", { count: lineCount })}</span>
        </div>
      </div>

      <style>{`
        .prompt-editor {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
        }

        .prompt-editor-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 15px;
          padding-bottom: 15px;
          border-bottom: 1px solid #e0e0e0;
        }

        .prompt-info h3 {
          margin: 0 0 5px 0;
          font-size: 18px;
          color: #333;
        }

        .prompt-description {
          margin: 5px 0;
          color: #666;
          font-size: 14px;
        }

        .prompt-meta {
          display: flex;
          gap: 10px;
          align-items: center;
          margin-top: 10px;
        }

        .prompt-category {
          background: #f0f0f0;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          color: #666;
        }

        .prompt-actions {
          display: flex;
          gap: 10px;
        }

        .prompt-variables {
          background: #f8f9fa;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          padding: 15px;
          margin-bottom: 15px;
        }

        .prompt-variables h4 {
          margin: 0 0 10px 0;
          font-size: 14px;
          color: #333;
        }

        .variables-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 10px;
        }

        .variable-tag {
          background: #e3f2fd;
          color: #1976d2;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-family: 'Courier New', monospace;
        }

        .variables-note {
          margin: 10px 0 0 0;
          font-size: 12px;
          color: #666;
          font-style: italic;
        }

        .message {
          padding: 10px 15px;
          border-radius: 4px;
          margin-bottom: 15px;
        }

        .message-success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .message-error {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .prompt-editor-body {
          position: relative;
        }

        .prompt-textarea {
          width: 100%;
          min-height: 400px;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          font-size: 13px;
          line-height: 1.5;
          resize: vertical;
        }

        .prompt-textarea:focus {
          outline: none;
          border-color: #1976d2;
          box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.1);
        }

        .prompt-stats {
          display: flex;
          gap: 15px;
          margin-top: 8px;
          font-size: 12px;
          color: #666;
        }
      `}</style>
    </div>
  );
};

export default PromptEditor;
